import { cva, type VariantProps } from "class-variance-authority";
import {
  type Component,
  createContext,
  createSignal,
  type JSX,
  Show,
  splitProps,
  useContext,
} from "solid-js";
import { cn } from "../../libs/cn";

const avatarVariants = cva(
  "rounded-full bg-gray-300 flex items-center justify-center",
  {
    variants: {
      size: {
        xs: "w-6 h-6",
        sm: "w-8 h-8",
        default: "w-10 h-10",
        lg: "w-12 h-12",
        xl: "w-14 h-14",
        full: "w-full h-full",
      },
    },
  },
);

type AvatarSize = VariantProps<typeof avatarVariants>["size"];

interface AvatarProps extends JSX.HTMLAttributes<HTMLSpanElement> {
  src: string;
  alt: string;
  size?: AvatarSize;
}

type AvatarContextType = AvatarProps & {
  imageLoading: "loading" | "loaded" | "error";
  setImageLoading: (value: "loading" | "loaded" | "error") => void;
};

const AvatarContext = createContext<AvatarContextType>({
  src: "",
  alt: "",
  imageLoading: "loading",
  setImageLoading: () => {},
});

const AvatarProvider = (props: AvatarProps) => {
  const [imageLoading, setImageLoading] = createSignal<
    "loading" | "loaded" | "error"
  >("loading");

  return (
    <AvatarContext.Provider
      value={{ ...props, imageLoading: imageLoading(), setImageLoading }}
    >
      {props.children}
    </AvatarContext.Provider>
  );
};

export const AvatarImage = () => {
  const props = useContext(AvatarContext);
  return (
    <Show when={props.imageLoading === "loaded"} fallback={null}>
      <img
        class={cn(avatarVariants({ size: props.size }))}
        src={props.src}
        alt={props.alt}
        onError={() => props.setImageLoading("error")}
        onLoad={() => props.setImageLoading("loaded")}
      />
    </Show>
  );
};

export const AvatarFallback = ({ children }: { children: JSX.Element }) => {
  const props = useContext(AvatarContext);
  return (
    <Show when={props.imageLoading !== "loaded"}>
      <div class={cn(avatarVariants({ size: props.size }))}>{children}</div>
    </Show>
  );
};

export const Avatar: Component<AvatarProps & { children: JSX.Element }> = (
  props,
) => {
  const [avatar, spanProps] = splitProps(props, [
    "children",
    "size",
    "src",
    "alt",
  ]);
  return (
    <AvatarProvider {...avatar}>
      <span {...spanProps}>{props.children}</span>
    </AvatarProvider>
  );
};

export const AvatarToDeprecate: Component<
  AvatarProps & { fallback: JSX.Element }
> = (props) => {
  const [avatar] = splitProps(props, ["src", "alt", "fallback", "class"]);
  return (
    <div
      class={cn(
        "w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden",
        avatar.class,
      )}
    >
      <Show when={avatar.src} fallback={avatar.fallback}>
        <img
          src={avatar.src ?? ""}
          alt={avatar.alt}
          class="w-full h-full object-cover rounded-full"
        />
      </Show>
    </div>
  );
};
